// Inventory.js - Main Inventory page for Attack Surface Inventory
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getInventory, downloadInventory } from '../services/inventoryService';
import InventoryTable from '../components/Inventory/InventoryTable';
import InventoryFilters from '../components/Inventory/InventoryFilters';
import EntitySummaryCards from '../components/Inventory/EntitySummaryCards';
import BulkActionBar from '../components/Inventory/BulkActionBar';
import EntityDrawer from '../components/Inventory/EntityDrawer';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import './Inventory.css';

function Inventory() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Pagination state
    const [continuationToken, setContinuationToken] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [hiddenCount, setHiddenCount] = useState(0);

    // Selection state for bulk actions
    const [selectedIds, setSelectedIds] = useState([]);

    // Assets list for filter dropdown
    const [assets, setAssets] = useState([]);

    // Initial asset filter from URL params (supports ?asset= or ?assetId=)
    const initialAssetId = searchParams.get('asset') || searchParams.get('assetId') || 'all';

    // Filter state
    const [filters, setFilters] = useState({
        type: 'all',
        assetId: initialAssetId,
        severity: 'all',
        state: 'active',
        hidden: 'false',
        newlySeen: '',
        search: '',
        serviceName: '',
        sort: 'lastSeenAt',
        order: 'desc',
        pageSize: '100'
    });

    // Facets for filter counts
    const [facets, setFacets] = useState({
        byType: {},
        bySeverity: {},
        byState: {}
    });

    // Tier/access state
    const [limits, setLimits] = useState({
        tier: 'free',
        inventoryEnabled: false,
        maxEntities: 0
    });

    // Load inventory data
    const loadInventory = useCallback(async (newFilters = filters, append = false) => {
        try {
            setLoading(true);
            if (!append) {
                setError(null);
            }

            const data = await getInventory({
                ...newFilters,
                continuationToken: append ? continuationToken : ''
            });

            if (append) {
                setItems(prev => [...prev, ...data.items]);
            } else {
                setItems(data.items || []);
            }

            setContinuationToken(data.continuationToken);
            setTotalCount(data.totalCount || 0);
            setHiddenCount(data.hiddenCount || 0);
            setFacets(data.facets || { byType: {}, bySeverity: {}, byState: {} });
            setLimits(data.limits || { tier: 'free', inventoryEnabled: false, maxEntities: 0 });

        } catch (err) {
            console.error('Failed to load inventory:', err);

            // Check if it's a tier access error
            if (err.statusCode === 403 && err.inventoryEnabled === false) {
                setLimits({
                    tier: err.tier || 'free',
                    inventoryEnabled: false,
                    maxEntities: 0,
                    upgradeMessage: err.upgradeMessage
                });
            } else {
                setError(err.message || 'Failed to load inventory');
            }
        } finally {
            setLoading(false);
        }
    }, [filters, continuationToken]);

    // Fetch assets for filter dropdown
    const loadAssets = useCallback(async () => {
        try {
            const response = await fetch('/api/asset-list', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAssets(data.assets || []);
            }
        } catch (err) {
            console.error('Failed to load assets:', err);
        }
    }, []);

    // Initial load
    useEffect(() => {
        loadInventory();
        loadAssets();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        setSelectedIds([]); // Clear selection on filter change
        loadInventory(newFilters, false);
    };

    // Handle selection changes
    const handleSelectionChange = (newSelectedIds) => {
        setSelectedIds(newSelectedIds);
    };

    // Handle search
    const handleSearch = (searchTerm) => {
        handleFilterChange('search', searchTerm);
    };

    // Handle load more
    const handleLoadMore = () => {
        if (continuationToken) {
            loadInventory(filters, true);
        }
    };

    // Handle row click
    const handleRowClick = (entity) => {
        setSelectedEntity(entity);
        setDrawerOpen(true);
    };

    // Handle drawer close
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setTimeout(() => setSelectedEntity(null), 300); // Clear after animation
    };

    // Handle export
    const handleExport = async (format) => {
        try {
            await downloadInventory(format, filters);
        } catch (err) {
            setError(`Export failed: ${err.message}`);
        }
    };

    // Handle bulk suppress
    const handleBulkSuppress = async () => {
        if (selectedIds.length === 0) return;

        try {
            const response = await fetch('/api/inventory/bulk-suppress', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ entityIds: selectedIds })
            });

            if (response.ok) {
                const result = await response.json();
                // Update local state to reflect suppression
                setItems(prev => prev.map(item =>
                    selectedIds.includes(item.entityId)
                        ? { ...item, isSuppressed: true }
                        : item
                ));
                setSelectedIds([]);
                // Show success message (could use toast)
                console.log(`Suppressed ${result.suppressedCount} entities`);
            } else {
                const errData = await response.json();
                setError(errData.message || 'Failed to suppress entities');
            }
        } catch (err) {
            setError(`Suppress failed: ${err.message}`);
        }
    };

    // Handle bulk export
    const handleBulkExport = async (format) => {
        if (selectedIds.length === 0) return;

        try {
            await downloadInventory(format, { ...filters, entityIds: selectedIds.join(',') });
        } catch (err) {
            setError(`Export failed: ${err.message}`);
        }
    };

    // Clear selection
    const handleClearSelection = () => {
        setSelectedIds([]);
    };

    // Handle refresh
    const handleRefresh = () => {
        loadInventory(filters, false);
    };

    // Calculate stats for header
    const totalEntities = totalCount;
    const findingsCount = facets.byType?.finding || 0;
    const criticalCount = facets.bySeverity?.critical || 0;
    const highCount = facets.bySeverity?.high || 0;

    // Check if user has access
    const hasAccess = limits.inventoryEnabled;

    return (
        <CleanModernLayout activePage="inventory">
        <div className="inventory-page">
            {/* Page Header */}
            <header className="inventory-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <span className="terminal-prompt">INVENTORY</span>
                    </h1>
                    <p className="page-subtitle">
                        Attack Surface Inventory • Complete Asset Discovery • Entity Relationships
                    </p>
                </div>

                <div className="header-actions">
                    {/* Stats Display */}
                    <div className="header-stats">
                        <div className="stat-item">
                            <span className="stat-value">{totalEntities.toLocaleString()}</span>
                            <span className="stat-label">Entities</span>
                        </div>
                        <div className="stat-item stat-findings">
                            <span className="stat-value">{findingsCount}</span>
                            <span className="stat-label">Findings</span>
                        </div>
                        {criticalCount > 0 && (
                            <div className="stat-item stat-critical">
                                <span className="stat-value">{criticalCount}</span>
                                <span className="stat-label">Critical</span>
                            </div>
                        )}
                        {highCount > 0 && (
                            <div className="stat-item stat-high">
                                <span className="stat-value">{highCount}</span>
                                <span className="stat-label">High</span>
                            </div>
                        )}
                    </div>

                    {/* Tier Badge */}
                    <span className={`tier-badge tier-${limits.tier}`}>
                        {limits.tier?.toUpperCase()}
                    </span>
                </div>
            </header>

            {/* Error Banner */}
            {error && (
                <div className="error-banner">
                    <span className="error-icon">⚠</span>
                    <span className="error-message">{error}</span>
                    <button className="error-dismiss" onClick={() => setError(null)}>×</button>
                </div>
            )}

            {/* Upgrade Banner for non-Pro users */}
            {!hasAccess && !loading && (
                <div className="upgrade-banner">
                    <div className="upgrade-content">
                        <span className="upgrade-icon">🔒</span>
                        <div className="upgrade-text">
                            <strong>Inventory requires Pro or Enterprise tier</strong>
                            <p>
                                {limits.upgradeMessage ||
                                    'Upgrade to access your complete attack surface inventory with entity relationships, advanced filtering, and export capabilities.'}
                            </p>
                        </div>
                        <button
                            className="btn-upgrade"
                            onClick={() => navigate('/upgrade')}
                        >
                            View Plans
                        </button>
                    </div>
                </div>
            )}

            {/* Main Content - Only show if user has access */}
            {hasAccess && (
                <>
                    {/* Entity Summary Cards */}
                    <EntitySummaryCards
                        facets={facets}
                        activeType={filters.type}
                        onTypeClick={(type) => handleFilterChange('type', type)}
                        totalCount={totalCount}
                    />

                    {/* Bulk Action Bar */}
                    <BulkActionBar
                        selectedCount={selectedIds.length}
                        onClearSelection={handleClearSelection}
                        onSuppress={handleBulkSuppress}
                        onExport={handleBulkExport}
                        loading={loading}
                    />

                    {/* Filters Bar */}
                    <InventoryFilters
                        filters={filters}
                        hiddenCount={hiddenCount}
                        onFilterChange={handleFilterChange}
                        onSearch={handleSearch}
                        onExport={handleExport}
                        onRefresh={handleRefresh}
                        loading={loading}
                    />

                    {/* Main Table */}
                    <main className="inventory-content">
                        {loading && items.length === 0 ? (
                            <div className="loading-state">
                                <div className="loading-spinner"></div>
                                <p>Loading inventory...</p>
                            </div>
                        ) : items.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">📦</div>
                                <h3>No Entities Found</h3>
                                <p>
                                    {filters.search || filters.type !== 'all' || filters.severity !== 'all'
                                        ? 'Try adjusting your filters to see more results.'
                                        : 'Your inventory will populate after your first asset baseline scan completes.'}
                                </p>
                            </div>
                        ) : (
                            <InventoryTable
                                items={items}
                                onRowClick={handleRowClick}
                                onLoadMore={handleLoadMore}
                                hasMore={!!continuationToken}
                                loading={loading}
                                totalCount={totalCount}
                                sort={filters.sort}
                                order={filters.order}
                                onSortChange={(field) => {
                                    const newOrder = filters.sort === field && filters.order === 'desc' ? 'asc' : 'desc';
                                    const newFilters = { ...filters, sort: field, order: newOrder };
                                    setFilters(newFilters);
                                    loadInventory(newFilters, false);
                                }}
                                filters={filters}
                                facets={facets}
                                assets={assets}
                                onFilterChange={handleFilterChange}
                                selectedIds={selectedIds}
                                onSelectionChange={handleSelectionChange}
                            />
                        )}
                    </main>
                </>
            )}

            {/* Entity Details Drawer */}
            {selectedEntity && (
                <EntityDrawer
                    entity={selectedEntity}
                    isOpen={drawerOpen}
                    onClose={handleDrawerClose}
                />
            )}
        </div>
        </CleanModernLayout>
    );
}

export default Inventory;
